import type { BoardSortHint } from "@design-system/Board/Board.types";
import { BOARD_PLACE_KEY_LABEL } from "@design-system/Board/boardSensors";
import {
    isTaskTerminatingStatus,
    TaskPriority,
    TaskStatus,
} from "@common/types/task";
import type { DemoTask } from "./fixtures/demoTasks";
import { isTaskOverdue } from "./taskBoardConfig";

export type TaskSort = "attention" | "dueDate" | "priority" | "created";

export const TASK_SORT_OPTIONS: ReadonlyArray<{
    value: TaskSort;
    label: string;
}> = [
    { value: "attention", label: "Needs attention" },
    { value: "dueDate", label: "Due date" },
    { value: "priority", label: "Priority" },
    { value: "created", label: "Created" },
];

/** What `task`, placed beside a neighbour, copies from it. */
type TaskSortInheritance = ((
    neighbour: DemoTask,
    task: DemoTask,
) => Partial<TaskSortKey>) & {
    /** The sort hint's line for it: what holding the place key updates. */
    placeLabel: string;
};

const withPlaceLabel = (
    placeLabel: string,
    inherit: (neighbour: DemoTask, task: DemoTask) => Partial<TaskSortKey>,
): TaskSortInheritance => Object.assign(inherit, { placeLabel });

/** The sort's label keeps its casing, as it reads in the Display popover. */
export const getTaskSortHint = (sort: TaskSort): BoardSortHint => {
    const inheritance = TASK_SORT_INHERITANCE[sort];
    return {
        title: `Board ordered by ${
            TASK_SORT_OPTIONS.find((option) => option.value === sort)?.label ??
            sort
        }`,
        ...(inheritance !== null && { placeLabel: inheritance.placeLabel }),
    };
};

const PRIORITY_RANKS: Record<TaskPriority, number> = {
    [TaskPriority.URGENT]: 0,
    [TaskPriority.HIGH]: 1,
    [TaskPriority.MEDIUM]: 2,
    [TaskPriority.LOW]: 3,
};

type AttentionGroup = "blocked" | "overdue" | "inProgress" | "toDo";

const ATTENTION_RANKS: Record<AttentionGroup, number> = {
    blocked: 0,
    overdue: 1,
    inProgress: 2,
    toDo: 3,
};

/** A group a task placed by hand stays in (`TASK_SORT_PLACE_GROUPS`). */
export type TaskPlaceGroup = AttentionGroup | "finished";

/** Each group's name in the reason a held drop is refused. */
export const TASK_PLACE_GROUP_LABELS: Record<TaskPlaceGroup, string> = {
    blocked: "blocked",
    overdue: "overdue",
    inProgress: "in progress",
    toDo: "to do",
    finished: "finished",
};

const getAttentionGroup = (
    task: DemoTask,
    todayDateOnly: string,
): AttentionGroup => {
    if (task.status === TaskStatus.BLOCKED) {
        return "blocked";
    }
    if (isTaskOverdue(task, todayDateOnly)) {
        return "overdue";
    }
    return task.status === TaskStatus.IN_PROGRESS ? "inProgress" : "toDo";
};

const getAttentionRank = (task: DemoTask, todayDateOnly: string) =>
    ATTENTION_RANKS[getAttentionGroup(task, todayDateOnly)];

/** Ascending, with missing values last. */
const compareOptional = <T extends string | number>(
    first: T | null,
    second: T | null,
) => {
    if (first === second) {
        return 0;
    }
    if (first === null) {
        return 1;
    }
    if (second === null) {
        return -1;
    }
    return first < second ? -1 : 1;
};

const compareDueDates = (first: DemoTask, second: DemoTask) =>
    compareOptional(first.dueDateOnly, second.dueDateOnly);

const getPriorityRank = (task: DemoTask) =>
    task.priority === null ? null : PRIORITY_RANKS[task.priority];

const COMPARATORS: Record<
    TaskSort,
    (todayDateOnly: string) => (first: DemoTask, second: DemoTask) => number
> = {
    attention: (todayDateOnly) => (first, second) =>
        getAttentionRank(first, todayDateOnly) -
            getAttentionRank(second, todayDateOnly) ||
        compareDueDates(first, second),
    dueDate: () => compareDueDates,
    priority: () => (first, second) =>
        compareOptional(getPriorityRank(first), getPriorityRank(second)) ||
        compareDueDates(first, second),
    created: () => (first, second) =>
        compareOptional(second.createdDateOnly, first.createdDateOnly),
};

export type TaskSortKey = Pick<DemoTask, "priority" | "dueDateOnly" | "status">;

// Finished tasks sink below the rest, so a task beside one only ties with it
// when it's finished too, and a finished task beside an open one only once
// it reopens to To do. Done, even beside a canceled task: a canceled draft
// would be locked.
const getFinishedStatus = (neighbour: DemoTask, task: DemoTask) => {
    if (isTaskTerminatingStatus(neighbour.status)) {
        return { status: TaskStatus.DONE };
    }
    return isTaskTerminatingStatus(task.status)
        ? { status: TaskStatus.TODO }
        : {};
};

/**
 * What a task placed beside a neighbour (a held drop, or a gap `+` draft)
 * copies from it so the sort shows it there: the due date, or the priority
 * and its due date tiebreak, plus Done when the neighbour is done or
 * canceled. Needs attention copies only the due date, which keeps the task
 * there only beside a neighbour in its own group (`TASK_SORT_PLACE_GROUPS`).
 * Null where no copied value can: Created sorts by creation date, which is
 * today.
 */
export const TASK_SORT_INHERITANCE: Record<
    TaskSort,
    TaskSortInheritance | null
> = {
    attention: withPlaceLabel(
        `Hold ${BOARD_PLACE_KEY_LABEL} to place among similar tasks (updates due date)`,
        (neighbour) => ({ dueDateOnly: neighbour.dueDateOnly }),
    ),
    dueDate: withPlaceLabel(
        `Hold ${BOARD_PLACE_KEY_LABEL} to also update due date`,
        (neighbour, task) => ({
            dueDateOnly: neighbour.dueDateOnly,
            ...getFinishedStatus(neighbour, task),
        }),
    ),
    priority: withPlaceLabel(
        `Hold ${BOARD_PLACE_KEY_LABEL} to also update priority and due date`,
        (neighbour, task) => ({
            priority: neighbour.priority,
            dueDateOnly: neighbour.dueDateOnly,
            ...getFinishedStatus(neighbour, task),
        }),
    ),
    created: null,
};

/**
 * Sorts that take a task placed by hand only beside a neighbour in the same
 * group (`TASK_PLACE_GROUP_LABELS` names it). Needs attention: blocked,
 * overdue, in progress, to do, or finished. A neighbour's due date never
 * moves a task out of its group: same status, and overdue only beside an
 * overdue task. Null where any neighbour will do.
 */
export const TASK_SORT_PLACE_GROUPS: Record<
    TaskSort,
    ((task: DemoTask, todayDateOnly: string) => TaskPlaceGroup) | null
> = {
    attention: (task, todayDateOnly) =>
        isTaskTerminatingStatus(task.status)
            ? "finished"
            : getAttentionGroup(task, todayDateOnly),
    dueDate: null,
    priority: null,
    created: null,
};

interface TaskPlaceSpot {
    sort: TaskSort;
    /** With its new column and lane values. */
    task: DemoTask;
    before: DemoTask | null;
    after: DemoTask | null;
    todayDateOnly: string;
}

/**
 * The neighbour a task placed by hand copies its sort key from: the card
 * above, else the one below, of its own group where the sort has groups.
 * Null when neither qualifies.
 */
export const findTaskPlaceNeighbour = ({
    sort,
    task,
    before,
    after,
    todayDateOnly,
}: TaskPlaceSpot) => {
    const getGroup = TASK_SORT_PLACE_GROUPS[sort];
    if (getGroup === null) {
        return before ?? after;
    }
    const group = getGroup(task, todayDateOnly);
    return (
        [before, after].find(
            (neighbour): neighbour is DemoTask =>
                neighbour !== null &&
                getGroup(neighbour, todayDateOnly) === group,
        ) ?? null
    );
};

/**
 * Card order for a sort; done and canceled tasks always sink to the bottom.
 * "Needs attention" is blocked, overdue, in progress, to do, then by due
 * date; Created is newest first.
 */
export const createTaskComparator = (sort: TaskSort, todayDateOnly: string) => {
    const compare = COMPARATORS[sort](todayDateOnly);
    return (first: DemoTask, second: DemoTask) =>
        Number(isTaskTerminatingStatus(first.status)) -
            Number(isTaskTerminatingStatus(second.status)) ||
        compare(first, second);
};
